import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import {
  Search, X, FileText, Loader2, ScrollText, User, Clock, Hash,
  PhoneIncoming, PhoneOutgoing, AlertTriangle,
} from "lucide-react";
import { toast } from "react-hot-toast";

const DEFAULT_TIMEZONE = "America/Los_Angeles";

function formatPT(dateStr: string) {
  try {
    return format(utcToZonedTime(new Date(dateStr), DEFAULT_TIMEZONE), "MMM d, yyyy h:mm a");
  } catch {
    return dateStr;
  }
}

function fmtDuration(seconds: number | null) {
  if (seconds === null) return "—";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s}s`;
}

interface CallSummary {
  call_id:            string;
  agent:              string | null;
  call_summary:       string | null;
  duration:           number | null;
  from_number:        string | null;
  to_number:          string | null;
  model:              string | null;
  numbers:            string | null;
  processed_at:       string | null;
  prompt_version:     string | null;
  status_reason:      string | null;
  time:               string | null;
  trace_id:           string | null;
  telephonyQueueName: string | null;
  direction:          string | null;
}

interface TranscriptContent {
  loading: boolean;
  data:    any | null;
  error:   string | null;
}

// ─── Modal ───────────────────────────────────────────────────────────────────

function CallModal({
  rec,
  onClose,
}: {
  rec: CallSummary;
  onClose: () => void;
}) {
  const [txState, setTxState] = useState<TranscriptContent>({ loading: false, data: null, error: null });
  const [requested, setRequested] = useState(false);

  const fetchTranscript = async () => {
    if (requested) return;
    setRequested(true);
    setTxState({ loading: true, data: null, error: null });
    try {
      const res = await fetch(`/api/v1/transcripts/${rec.call_id}/content`);
      if (res.status === 404) {
        setTxState({ loading: false, data: null, error: "No transcript found in S3." });
        return;
      }
      if (!res.ok) throw new Error("Failed");
      const json = await res.json();
      setTxState({ loading: false, data: json.data, error: null });
    } catch {
      setTxState({ loading: false, data: null, error: "Failed to load transcript." });
    }
  };

  // Close on backdrop click or Escape key
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Prevent body scroll while open
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const hasSummary = !!rec.call_summary;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.45)" }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-base font-semibold text-gray-800">
              {rec.telephonyQueueName ?? "Call Details"}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">Call ID: {rec.call_id}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors ml-4 flex-shrink-0"
          >
            <X size={20} />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">

          {/* Meta chips */}
          <div className="flex flex-wrap gap-2">
            {/* Direction badge */}
            {rec.direction && (
              <span className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-medium ${
                rec.direction.toLowerCase() === "inbound"
                  ? "bg-green-50 text-green-700"
                  : "bg-purple-50 text-purple-700"
              }`}>
                {rec.direction.toLowerCase() === "inbound"
                  ? <PhoneIncoming size={12} />
                  : <PhoneOutgoing size={12} />
                }
                {rec.direction.charAt(0).toUpperCase() + rec.direction.slice(1).toLowerCase()}
              </span>
            )}
            {[
              { icon: User,  label: rec.agent ?? "No agent" },
              { icon: Clock, label: rec.time ? formatPT(rec.time) : "—" },
              { icon: Hash,  label: fmtDuration(rec.duration) },
            ].map(({ icon: Icon, label }) => (
              <div
                key={label}
                className="flex items-center gap-1.5 bg-gray-100 text-gray-600 text-xs px-3 py-1.5 rounded-full"
              >
                <Icon size={12} className="text-gray-400" />
                {label}
              </div>
            ))}
          </div>

          {/* From / To */}
          {(rec.from_number || rec.to_number) && (
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-gray-50 rounded-lg px-4 py-2.5">
                <div className="text-xs text-gray-400 mb-0.5">From</div>
                <div className="text-sm font-mono text-gray-700">{rec.from_number ?? "—"}</div>
              </div>
              <div className="bg-gray-50 rounded-lg px-4 py-2.5">
                <div className="text-xs text-gray-400 mb-0.5">To</div>
                <div className="text-sm font-mono text-gray-700">{rec.to_number ?? "—"}</div>
              </div>
            </div>
          )}

          {/* AI Summary */}
          {hasSummary ? (
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <FileText size={14} className="text-blue-500" />
                <span className="text-xs font-semibold text-blue-600 uppercase tracking-wide">AI Summary</span>
                {rec.processed_at && (
                  <span className="text-xs text-gray-400 ml-auto">
                    Processed {formatPT(rec.processed_at)}
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">{rec.call_summary}</p>
            </div>
          ) : (
            <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 text-sm text-gray-400 italic">
              No AI summary available for this call.
            </div>
          )}

          {/* Transcript section */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Transcript</span>
              {!requested && (
                <button
                  onClick={fetchTranscript}
                  className="flex items-center gap-1.5 text-xs bg-gray-800 hover:bg-gray-700 text-white px-3 py-1.5 rounded-lg font-medium transition-colors"
                >
                  <ScrollText size={12} /> View Transcript
                </button>
              )}
            </div>

            {!requested && (
              <p className="text-sm text-gray-400 italic">
                Click "View Transcript" to load from S3.
              </p>
            )}

            {txState.loading && (
              <div className="flex items-center gap-2 text-sm text-gray-400 py-4">
                <Loader2 size={14} className="animate-spin" /> Loading transcript…
              </div>
            )}

            {txState.error && (
              <p className="text-sm text-gray-400 italic">{txState.error}</p>
            )}

            {txState.data && <TranscriptViewer data={txState.data} />}
          </div>

        </div>
      </div>
    </div>
  );
}

// ─── Transcript renderer ──────────────────────────────────────────────────────

function TranscriptViewer({ data }: { data: any }) {
  // Our S3 format: { call_id, transcript, transcript_length, trace_id, saved_at }
  if (typeof data?.transcript === "string") {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 max-h-80 overflow-y-auto text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
        {data.transcript}
      </div>
    );
  }
  // AWS Transcribe format: { results: { transcripts: [{ transcript }] } }
  if (data?.results?.transcripts?.[0]?.transcript) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 max-h-80 overflow-y-auto text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
        {data.results.transcripts[0].transcript}
      </div>
    );
  }
  // Plain string
  if (typeof data === "string") {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 max-h-80 overflow-y-auto text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
        {data}
      </div>
    );
  }
  // Fallback: raw JSON
  return (
    <pre className="bg-gray-50 border border-gray-200 rounded-xl p-4 max-h-80 overflow-auto text-xs text-gray-600">
      {JSON.stringify(data, null, 2)}
    </pre>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function Transcripts() {
  const navigate = useNavigate();
  const LIMIT = 20;

  const [searchFilter, setSearchFilter] = useState("");
  const [records, setRecords]           = useState<CallSummary[]>([]);
  const [page, setPage]                 = useState(1);
  const [totalPages, setTotalPages]     = useState(1);
  const [totalCount, setTotalCount]     = useState(0);
  const [loading, setLoading]           = useState(false);
  const [selected, setSelected]         = useState<CallSummary | null>(null);

  const fetchRecords = useCallback(async (p: number) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(LIMIT), page: String(p) });
      const res = await fetch(`/api/v1/transcripts?${params}`);
      if (!res.ok) throw new Error("Failed to fetch");
      const json = await res.json();
      setRecords(json.data ?? []);
      setPage(json.pagination.page);
      setTotalPages(json.pagination.pages);
      setTotalCount(json.pagination.total);
    } catch {
      toast.error("Failed to load records");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRecords(1); }, [fetchRecords]);

  const visible = searchFilter.trim()
    ? records.filter((r) =>
        r.agent?.toLowerCase().includes(searchFilter.trim().toLowerCase()) ||
        r.telephonyQueueName?.toLowerCase().includes(searchFilter.trim().toLowerCase())
      )
    : records;

  const rangeStart = (page - 1) * LIMIT + 1;
  const rangeEnd   = Math.min(page * LIMIT, totalCount);

  return (
    <>
      {/* Modal */}
      {selected && (
        <CallModal rec={selected} onClose={() => setSelected(null)} />
      )}

      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-semibold text-gray-800">Call Transcripts & Summaries</h1>
          <button
            onClick={() => navigate("/transcripts/failed")}
            className="inline-flex items-center gap-2 rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
          >
            <AlertTriangle className="h-4 w-4" />
            View Failed Calls
          </button>
        </div>

        {/* Filter bar */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-5">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-600">Search</label>
              <input
                type="text"
                placeholder="Filter by agent or queue..."
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-64"
              />
            </div>
            <button
              onClick={() => { setSearchFilter(""); fetchRecords(1); }}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
            >
              <Search size={14} /> Refresh
            </button>
            {searchFilter && (
              <button
                onClick={() => setSearchFilter("")}
                className="flex items-center gap-2 text-gray-500 hover:text-gray-700 px-3 py-2 rounded-lg text-sm border border-gray-200"
              >
                <X size={14} /> Clear
              </button>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <Loader2 className="animate-spin text-blue-500" size={32} />
            </div>
          ) : visible.length === 0 ? (
            <div className="text-center py-20 text-gray-400">No records found</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Direction</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Queue</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Agent</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">From</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">To</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Time (PT)</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Duration</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Summary</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((rec) => (
                  <tr
                    key={rec.call_id}
                    onClick={() => setSelected(rec)}
                    className="border-b border-gray-100 cursor-pointer hover:bg-blue-50 transition-colors"
                  >
                    <td className="px-4 py-3">
                      {rec.direction ? (
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${
                          rec.direction.toLowerCase() === "inbound"
                            ? "bg-green-50 text-green-700"
                            : "bg-purple-50 text-purple-700"
                        }`}>
                          {rec.direction.toLowerCase() === "inbound"
                            ? <PhoneIncoming size={10} />
                            : <PhoneOutgoing size={10} />
                          }
                          {rec.direction.charAt(0).toUpperCase() + rec.direction.slice(1).toLowerCase()}
                        </span>
                      ) : <span className="text-gray-300 text-xs">—</span>}
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-800">
                      {rec.telephonyQueueName ?? "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {rec.agent ?? "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-500 font-mono text-xs">
                      {rec.from_number ?? "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-500 font-mono text-xs">
                      {rec.to_number ?? "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                      {rec.time ? formatPT(rec.time) : "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                      {fmtDuration(rec.duration)}
                    </td>
                    <td className="px-4 py-3">
                      {rec.call_summary ? (
                        <span className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">
                          <FileText size={10} /> AI Summary
                        </span>
                      ) : (
                        <span className="text-xs text-gray-300">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-col items-center gap-2 mt-5">
            <div className="flex items-center gap-3">
              <button
                disabled={page <= 1}
                onClick={() => fetchRecords(page - 1)}
                className="px-4 py-2 text-sm rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50 font-medium"
              >
                ← Previous
              </button>

              {/* Page pills */}
              <div className="flex items-center gap-1">
                {/* First page */}
                {page > 2 && (
                  <>
                    <button onClick={() => fetchRecords(1)} className="w-8 h-8 text-xs rounded-lg text-gray-500 hover:bg-gray-100">1</button>
                    {page > 3 && <span className="text-xs text-gray-300 px-1">…</span>}
                  </>
                )}
                {/* Page before current */}
                {page > 1 && (
                  <button onClick={() => fetchRecords(page - 1)} className="w-8 h-8 text-xs rounded-lg text-gray-500 hover:bg-gray-100">
                    {page - 1}
                  </button>
                )}
                {/* Current page */}
                <span className="w-8 h-8 text-xs rounded-lg bg-blue-600 text-white flex items-center justify-center font-semibold">
                  {page}
                </span>
                {/* Page after current */}
                {page < totalPages && (
                  <button onClick={() => fetchRecords(page + 1)} className="w-8 h-8 text-xs rounded-lg text-gray-500 hover:bg-gray-100">
                    {page + 1}
                  </button>
                )}
                {/* Last page */}
                {page < totalPages - 1 && (
                  <>
                    {page < totalPages - 2 && <span className="text-xs text-gray-300 px-1">…</span>}
                    <button onClick={() => fetchRecords(totalPages)} className="w-8 h-8 text-xs rounded-lg text-gray-500 hover:bg-gray-100">
                      {totalPages}
                    </button>
                  </>
                )}
              </div>

              <button
                disabled={page >= totalPages}
                onClick={() => fetchRecords(page + 1)}
                className="px-4 py-2 text-sm rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50 font-medium"
              >
                Next →
              </button>
            </div>

            <p className="text-xs text-gray-400">
              Showing {rangeStart}–{rangeEnd} of {totalCount} records
            </p>
          </div>
        )}
      </div>
    </>
  );
}
